import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X, Lock, Home, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

// Composant sélecteur de langue
const LanguageSelector = () => {
  const { currentLanguage, supportedLanguages, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLangInfo = supportedLanguages.find(l => l.code === currentLanguage);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-1 text-sm font-medium hover:text-white transition-colors"
        style={{ 
          fontFamily: '"Lora", serif',
          color: 'rgb(196, 150, 96)'
        }}
      >
        <Globe className="w-4 h-4 mr-1" />
        <span className="mr-1">{currentLangInfo?.flag}</span>
        <span className="hidden sm:inline">{currentLangInfo?.name}</span>
        <ChevronDown className="w-3 h-3 ml-1" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50 py-2">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  changeLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                  currentLanguage === lang.code ? 'bg-gray-50 font-semibold' : ''
                }`}
                style={{ fontFamily: '"Lora", serif' }}
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
                {currentLanguage === lang.code && (
                  <span className="ml-auto text-green-600">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const HeaderNew = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState({
    histoire: false,
    actualite: false,
    association: false,
    publications: false
  });
  const { currentLanguage, supportedLanguages, changeLanguage } = useLanguage();

  const toggleMobileDropdown = (dropdown) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  // Traductions du menu
  const { translatedText: homeTranslated } = useTranslate("Accueil");
  const { translatedText: associationTranslated } = useTranslate("Association");
  const { translatedText: historyTranslated } = useTranslate("Histoire");
  const { translatedText: newsMenuTranslated } = useTranslate("Actualité");
  const { translatedText: familiesTranslated } = useTranslate("Les 7 lignages");
  const { translatedText: publicationsTranslated } = useTranslate("Publications");
  const { translatedText: contactTranslated } = useTranslate("Contact");
  const { translatedText: memberAreaTranslated } = useTranslate("Espace membres");
  
  // Sous-menus Association
  const { translatedText: lignagesBxlTranslated } = useTranslate("Les lignages de Bruxelles");
  const { translatedText: constitutionTranslated } = useTranslate("Constitution et But");
  const { translatedText: councilTranslated } = useTranslate("Organe d'administration");
  const { translatedText: becomeMemberTranslated } = useTranslate("Devenir membre");
  
  // Sous-menus Histoire
  const { translatedText: historyLignagesTranslated } = useTranslate("Histoire des Lignages");
  const { translatedText: prerogativesTranslated } = useTranslate("Les prérogatives");
  const { translatedText: jeanIITranslated } = useTranslate("Jean II de Brabant");
  const { translatedText: familiesBlasonsTranslated } = useTranslate("Les Familles et blasons");
  
  // Sous-menus Actualité
  const { translatedText: eventsTranslated } = useTranslate("Événements");
  const { translatedText: newsTranslated } = useTranslate("News");
  const { translatedText: eventsUpcomingTranslated } = useTranslate("Événements à venir");
  const { translatedText: lesNewsTranslated } = useTranslate("Les News");
  
  // Sous-menus Publications
  const { translatedText: bulletinTranslated } = useTranslate("Le Bulletin");
  // "Le Valet" ne doit JAMAIS être traduit - c'est un nom propre
  const valetTranslated = "Le Valet";
  const { translatedText: filiationsTranslated } = useTranslate("Les filiations lignagères");
  const { translatedText: vademecumTranslated } = useTranslate("Vade-mecum");

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleDropdownToggle = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Effet pour détecter le scroll
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent backdrop-blur-md">
      {/* Top row - Patronage */}
      <div className="w-full bg-black bg-opacity-60 border-b border-white border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div 
              className="text-sm font-medium"
              style={{ 
                fontFamily: '"Times New Roman", Georgia, serif',
                color: 'rgb(196, 150, 96)'
              }}
            >
              {/* Desktop: une seule ligne */}
              <span className="hidden md:inline">Sous le haut patronage de Sa Majesté le Roi</span>
              {/* Mobile: deux lignes */}
              <span className="md:hidden text-xs leading-tight">
                Sous le haut patronage<br />de Sa Majesté le Roi
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Language Selector */}
              <LanguageSelector />
              
              {/* Members space */}
              <Link
                to="/membre"
                className="flex items-center px-3 py-1 text-sm font-medium hover:text-white transition-colors"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                <Lock className="w-4 h-4 mr-1" />
                {/* Desktop: icône + texte */}
                <span className="hidden md:inline">{memberAreaTranslated}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation row */}
      <div 
        className={`w-full bg-black bg-opacity-40 transition-all duration-300 ${
          isScrolled ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-40 opacity-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="hidden lg:grid lg:grid-cols-3 items-center py-3">
            {/* Left menu - Desktop only */}
            <div className="flex items-center space-x-2 justify-start">
              {/* Home icon */}
              <Link 
                to="/" 
                className="px-3 py-2 flex items-center hover:bg-black hover:bg-opacity-20 transition-all"
                style={{ color: 'rgb(196, 150, 96)' }}
              >
                <Home className="w-4 h-4" />
              </Link>

              {/* Histoire des Lignages dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setOpenDropdown('lignages')}
                  onMouseLeave={() => setOpenDropdown(null)}
                  className="px-4 py-2 font-bold text-lg flex items-center hover:bg-black hover:bg-opacity-20 transition-all"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {historyTranslated}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {openDropdown === 'lignages' && (
                  <div 
                    onMouseEnter={() => setOpenDropdown('lignages')}
                    onMouseLeave={() => setOpenDropdown(null)}
                    className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-xl py-2 z-50"
                  >
                    <Link
                      to="/lignages"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {historyLignagesTranslated}
                    </Link>
                    <Link
                      to="/prerogatives"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {prerogativesTranslated}
                    </Link>
                    <Link
                      to="/jean-ii-brabant"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {jeanIITranslated}
                    </Link>
                  </div>
                )}
              </div>

              {/* Familles et Blasons - Menu principal */}
              <Link 
                to="/familles-blasons" 
                className="px-4 py-2 font-bold text-lg hover:bg-black hover:bg-opacity-20 transition-all whitespace-nowrap"
                style={{ 
                  fontFamily: '"Times New Roman", Georgia, serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {familiesTranslated}
              </Link>

              {/* Actualité dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setOpenDropdown('actualite')}
                  onMouseLeave={() => setOpenDropdown(null)}
                  className="px-4 py-2 font-bold text-lg flex items-center hover:bg-black hover:bg-opacity-20 transition-all"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {newsMenuTranslated}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {openDropdown === 'actualite' && (
                  <div 
                    onMouseEnter={() => setOpenDropdown('actualite')}
                    onMouseLeave={() => setOpenDropdown(null)}
                    className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-xl py-2 z-50"
                  >
                    <a
                      href="/#news"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {newsTranslated}
                    </a>
                    <a
                      href="/#event"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {eventsTranslated}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Logo center - Desktop only */}
            <div className="flex justify-center flex-shrink-0">
              <Link to="/">
                <img 
                  src="/logo.png" 
                  alt="Lignages de Bruxelles" 
                  className="w-24 h-24 object-contain drop-shadow-xl"
                />
              </Link>
            </div>

            {/* Right menu - Desktop only */}
            <div className="flex items-center space-x-2 justify-end">
              {/* Association dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setOpenDropdown('association')}
                  onMouseLeave={() => setOpenDropdown(null)}
                  className="px-4 py-2 font-bold text-lg flex items-center hover:bg-black hover:bg-opacity-20 transition-all"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {associationTranslated}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {openDropdown === 'association' && (
                  <div 
                    onMouseEnter={() => setOpenDropdown('association')}
                    onMouseLeave={() => setOpenDropdown(null)}
                    className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-xl py-2 z-50"
                  >
                    <Link
                      to="/association"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {lignagesBxlTranslated}
                    </Link>
                    <Link
                      to="/but"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {constitutionTranslated}
                    </Link>
                    <Link
                      to="/conseil"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {councilTranslated}
                    </Link>
                    <Link
                      to="/admission"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {becomeMemberTranslated}
                    </Link>
                  </div>
                )}
              </div>

              {/* Publications dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setOpenDropdown('publications')}
                  onMouseLeave={() => setOpenDropdown(null)}
                  className="px-4 py-2 font-bold text-lg flex items-center hover:bg-black hover:bg-opacity-20 transition-all"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {publicationsTranslated}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {openDropdown === 'publications' && (
                  <div 
                    onMouseEnter={() => setOpenDropdown('publications')}
                    onMouseLeave={() => setOpenDropdown(null)}
                    className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-xl py-2 z-50"
                  >
                    <Link
                      to="/publications/bulletin"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {bulletinTranslated}
                    </Link>
                    <Link
                      to="/publications/valet"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {valetTranslated}
                    </Link>
                    <Link
                      to="/publications/filiations"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {filiationsTranslated}
                    </Link>
                    <Link
                      to="/publications/vademecum"
                      className="block px-4 py-3 text-lg font-bold hover:bg-gray-100"
                      style={{ 
                        fontFamily: '"Times New Roman", Georgia, serif',
                        color: '#2c3e50'
                      }}
                      onClick={closeDropdown}
                    >
                      {vademecumTranslated}
                    </Link>
                  </div>
                )}
              </div>

              {/* Contact */}
              <Link 
                to="/contact" 
                className="px-4 py-2 font-bold text-lg hover:bg-black hover:bg-opacity-20 transition-all"
                style={{ 
                  fontFamily: '"Times New Roman", Georgia, serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {contactTranslated}
              </Link>
            </div>
          </nav>

          {/* Mobile: Logo centered + burger menu */}
          <nav className="flex lg:hidden items-center justify-between py-3">
            <div></div> {/* Spacer */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Logo Lignages" 
                className="h-16 w-auto"
              />
            </Link>
            <button
              onClick={toggleMenu}
              className="p-2 rounded hover:bg-white hover:bg-opacity-10"
              style={{ color: 'rgb(196, 150, 96)' }}
              aria-label="Menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="lg:hidden pb-4 bg-black bg-opacity-95">
              <div className="space-y-1 px-4 py-2">

                {/* Home */}
                <Link 
                  to="/" 
                  className="flex items-center px-3 py-2 text-base font-medium hover:bg-white hover:bg-opacity-10 rounded"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                  onClick={toggleMenu}
                >
                  <Home className="w-4 h-4 mr-2" />
                  {homeTranslated}
                </Link>

                {/* Histoire */}
                <div className="py-1">
                  <button
                    onClick={() => toggleMobileDropdown('histoire')}
                    className="w-full flex items-center justify-between px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                    style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }}
                  >
                    {historyTranslated}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdowns.histoire ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdowns.histoire && (
                    <div className="pl-4">
                      <Link to="/lignages" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {historyLignagesTranslated}
                      </Link>
                      <Link to="/prerogatives" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {prerogativesTranslated}
                      </Link>
                      <Link to="/jean-ii-brabant" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {jeanIITranslated}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Familles et Blasons - Menu principal mobile */}
                <Link 
                  to="/familles-blasons" 
                  className="block px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                  onClick={toggleMenu}
                >
                  {familiesTranslated}
                </Link>

                {/* Actualité */}
                <div className="py-1">
                  <button
                    onClick={() => toggleMobileDropdown('actualite')}
                    className="w-full flex items-center justify-between px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                    style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }}
                  >
                    {newsMenuTranslated}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdowns.actualite ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdowns.actualite && (
                    <div className="pl-4">
                      <a href="/#news" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {lesNewsTranslated}
                      </a>
                      <a href="/#event" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {eventsUpcomingTranslated}
                      </a>
                    </div>
                  )}
                </div>

                {/* Association */}
                <div className="py-1">
                  <button
                    onClick={() => toggleMobileDropdown('association')}
                    className="w-full flex items-center justify-between px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                    style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }}
                  >
                    {associationTranslated}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdowns.association ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdowns.association && (
                    <div className="pl-4">
                      <Link to="/association" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {lignagesBxlTranslated}
                      </Link>
                      <Link to="/but" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {constitutionTranslated}
                      </Link>
                      <Link to="/conseil" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {councilTranslated}
                      </Link>
                      <Link to="/admission" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {becomeMemberTranslated}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Publications */}
                <div className="py-1">
                  <button
                    onClick={() => toggleMobileDropdown('publications')}
                    className="w-full flex items-center justify-between px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                    style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }}
                  >
                    {publicationsTranslated}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdowns.publications ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdowns.publications && (
                    <div className="pl-4">
                      <Link to="/publications/bulletin" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {bulletinTranslated}
                      </Link>
                      <Link to="/publications/valet" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {valetTranslated}
                      </Link>
                      <Link to="/publications/filiations" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {filiationsTranslated}
                      </Link>
                      <Link to="/publications/vademecum" className="block px-6 py-2 text-sm hover:bg-white hover:bg-opacity-10" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }} onClick={toggleMenu}>
                        {vademecumTranslated}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Contact */}
                <Link 
                  to="/contact" 
                  className="block px-3 py-2 text-base font-bold hover:bg-white hover:bg-opacity-10 rounded"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                  onClick={toggleMenu}
                >
                  {contactTranslated}
                </Link>

                {/* Séparateur */}
                <div className="border-t border-white border-opacity-20 my-2"></div>

                {/* Espace Membres */}
                <Link 
                  to="/membre" 
                  className="flex items-center px-3 py-2 text-base font-medium hover:bg-white hover:bg-opacity-10 rounded"
                  style={{ 
                    fontFamily: '"Times New Roman", Georgia, serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                  onClick={toggleMenu}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {memberAreaTranslated}
                </Link>

                {/* Language Selector Mobile */}
                <div className="px-3 py-2">
                  <div className="text-sm font-bold mb-2" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Times New Roman", Georgia, serif' }}>
                    <Globe className="w-4 h-4 inline mr-2" />
                    Langue / Taal / Language
                  </div>
                  <div className="flex items-center justify-center gap-2 px-2">
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          toggleMenu();
                        }}
                        className={`text-2xl p-2 rounded hover:bg-white hover:bg-opacity-20 transition-all ${
                          currentLanguage === lang.code ? 'bg-white bg-opacity-20 ring-2 ring-white ring-opacity-30' : ''
                        }`}
                        title={lang.name}
                      >
                        {lang.flag}
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </header>

    {/* Bouton Scroll to Top */}
    {isScrolled && (
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        style={{
          backgroundColor: 'rgb(196, 150, 96)',
          color: 'white'
        }}
        aria-label="Retour en haut"
        title="Retour en haut"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="w-6 h-6" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M5 10l7-7m0 0l7 7m-7-7v18" 
          />
        </svg>
      </button>
    )}
    </>
  );
};

export default HeaderNew;
